import { client } from '@/api/http';
import { AxiosError, type AxiosRequestConfig, type AxiosResponse, HttpStatusCode } from 'axios';
import type {
    SurgicalGuideCaseSettingsApiObject,
    SurgicalGuideUserPreferencesApiObject,
    UpdateSurgicalGuideCaseSettingsRequest,
    UpdateSurgicalGuideUserPreferencesRequest,
} from '@/api/surgicalGuide/types';

export async function getCaseSettings(
    caseId: number,
    config?: AxiosRequestConfig,
): Promise<SurgicalGuideCaseSettingsApiObject | null> {
    const { status, data }: AxiosResponse = await client.get(
        `/cases/${caseId}/surgical-guide-preferences`,
        config,
    );
    if (status === HttpStatusCode.Ok) {
        return data;
    } else if (status === HttpStatusCode.NotFound) {
        return null;
    }

    throw new AxiosError(`Failed to load surgical guide case settings: ${data?.detail}`);
}

export async function putCaseSettings(
    caseId: number,
    body: UpdateSurgicalGuideCaseSettingsRequest,
    config?: AxiosRequestConfig,
): Promise<void> {
    const { status, data }: AxiosResponse = await client.put(
        `/cases/${caseId}/surgical-guide-preferences`,
        body,
        config,
    );
    if (status === HttpStatusCode.NoContent) {
        return;
    }

    throw new AxiosError(`Failed to save surgical guide case settings: ${data?.detail}`);
}

export async function getUserPreferences(
    surgeonId: number,
    config?: AxiosRequestConfig,
): Promise<SurgicalGuideUserPreferencesApiObject | null> {
    const { status, data }: AxiosResponse = await client.get(
        `users/${surgeonId}/surgical-guide-user-preferences`,
        config,
    );
    if (status === HttpStatusCode.Ok) {
        return data;
    } else if (status === HttpStatusCode.NotFound) {
        return null;
    }

    throw new AxiosError(`Failed to load surgical guide preferences: ${data?.detail}`);
}

export async function putUserPreferences(
    surgeonId: number,
    body: UpdateSurgicalGuideUserPreferencesRequest,
    config?: AxiosRequestConfig,
): Promise<void> {
    const { status, data }: AxiosResponse = await client.put(
        `users/${surgeonId}/surgical-guide-user-preferences`,
        body,
        config,
    );
    if (status === HttpStatusCode.NoContent) {
        return;
    }

    throw new AxiosError(`Failed to save surgical guide preferences: ${data?.detail}`);
}
